// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

// This function returns the status text corresponding to an orderStatus
/* eslint-disable import/prefer-default-export */
export const GetOrderStatus = (status: number) =>
  (status === 0 && 'Created') ||
  (status === 1 && 'Processing') ||
  (status === 2 && 'Autonomous successful') ||
  (status === 3 && 'Autonomous failed') ||
  (status === 4 && 'Manual drafting') ||
  (status === 5 && 'US QC') ||
  (status === 6 && 'Ready to Upload') ||
  (status === 7 && 'Done') ||
  (status === 8 && 'Canceled');

export type CustomerOrderStatus = 'Sourcing data' | 'Awaiting your data' | 'Data received' | 'Processing' | 'Completed' | 'Canceled' | 'Estimate';

// What "Data received" tells the customer on hover: more data is welcome, none is needed (Joel, 2026-10-16).
export const DATA_RECEIVED_NOTE = 'You can add more data until processing begins. We\'ll email you when it does.';

// The words a KML-only hold reads until Start Processing launches it (AUT-6266): "Sourcing data" while AirWorks
// sources the data; for a customer who chose to upload its own, "Awaiting your data", then "Data received" once a
// file of theirs has passed its EPSG check (the AUT-6266 check).
const kmlOnlyHoldStatus = (kmlOnlyOwnData?: boolean, ownDataReceived?: boolean): CustomerOrderStatus => {
  if (!kmlOnlyOwnData) return 'Sourcing data';
  return ownDataReceived ? 'Data received' : 'Awaiting your data';
};

type KmlOnlyHoldFields = {
  orderStatus?: number | null;
  kmlOnly?: boolean;
  kmlOnlyOwnData?: boolean;
  kmlOnlyLaunched?: boolean;
  ownDataReceived?: boolean;
};

// A KML-only order's pipeline is deferred at placement and its clock hasn't started. Start Processing keeps it at
// orderStatus 1 and marks the launch (kmlOnlyLaunched, from the order's launchedAt), so the hold's words give way to
// the order's progress at the launch, not when the pipeline later moves the status on. Null for any other order. The
// dashboard's chips read it as well as the project page and Order History (AUT-6630).
export const kmlOnlyHoldWord = ({
  orderStatus, kmlOnly, kmlOnlyOwnData, kmlOnlyLaunched, ownDataReceived,
}: KmlOnlyHoldFields): CustomerOrderStatus | null => (
  kmlOnly && orderStatus === 1 && !kmlOnlyLaunched ? kmlOnlyHoldStatus(kmlOnlyOwnData, ownDataReceived) : null
);

// Customer-facing status words — the vocabulary the project sidebar badge has always used, shared with
// Order History now that in-flight orders list there (AUT-6526). GetOrderStatus above is the admin
// dropdown's internal vocabulary and stays off customer surfaces. Returns null when the order carries
// no state to name (an unplaced non-estimate, or a row from a server that sends no orderStatus) —
// callers render a dash, never a guessed state.
export const getCustomerOrderStatus = ({
  orderStatus,
  kmlOnly,
  kmlOnlyOwnData,
  kmlOnlyLaunched,
  ownDataReceived,
  isEstimate,
}: Pick<IOrder, 'orderStatus' | 'kmlOnly' | 'kmlOnlyOwnData' | 'kmlOnlyLaunched' | 'ownDataReceived' | 'isEstimate'>): CustomerOrderStatus | null => {
  if (orderStatus === 7) return 'Completed';
  if (orderStatus === 8) return 'Canceled';
  const holdWord = kmlOnlyHoldWord({
    orderStatus, kmlOnly, kmlOnlyOwnData, kmlOnlyLaunched, ownDataReceived,
  });
  if (holdWord) return holdWord;
  if (orderStatus !== undefined && orderStatus > 0 && orderStatus < 7) return 'Processing';
  if (isEstimate) return 'Estimate';
  return null;
};

// A KML-only hold waiting on the customer's own upload rather than on data AirWorks sources (the AUT-6266 check).
export const isOwnDataHold = (order?: Pick<IOrder, 'kmlOnly' | 'kmlOnlyOwnData'> | null): boolean => !!(order?.kmlOnly && order?.kmlOnlyOwnData);

// A KML-only order has launched once Start Processing stamped it (kmlOnlyLaunched) or the launch filed its ops card
// (opsJiraId, served to Treasure Chest only), whichever the page sees first. Treasure Chest then offers Re-try
// Processing and retires its hold banner: the card arrives seconds after the press, and until the stamp was read the
// page kept offering a second start (AUT-6630 release close-out).
export const hasKmlOnlyLaunched = (order?: Pick<IOrder, 'opsJiraId' | 'kmlOnlyLaunched'> | null): boolean => !!(order?.opsJiraId || order?.kmlOnlyLaunched);
